/**
 * First-party, cookieless analytics.
 *
 * No cookies, no localStorage identifiers, no third-party scripts.
 * The edge function derives an anonymous daily visitor hash server-side and
 * never stores IP addresses.
 */

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-event`;

export type AnalyticsEvent =
  | 'page_view'
  | 'league_table_view'
  | 'manager_view'
  | 'compare_run'
  | 'live_view'
  | 'seo_page_view'
  | 'club_page_view';

function shouldTrack(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  // Respect Do Not Track / Global Privacy Control
  const dnt =
    (navigator as unknown as { doNotTrack?: string }).doNotTrack ??
    (window as unknown as { doNotTrack?: string }).doNotTrack;
  if (dnt === '1' || dnt === 'yes') return false;
  if ((navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl) return false;

  const host = window.location.hostname;
  // Skip local dev and Lovable preview/sandbox hosts — only measure the real site.
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) return false;
  // Skip local dev sandboxes and Lovable preview hosts, but DO measure the
  // published Lovable URL (e.g. tenure-tables.lovable.app).
  if (host.endsWith('.lovableproject.com')) return false;
  if (host.startsWith('id-preview--') || host.includes('.sandbox.')) return false;

  // Never measure admin tooling.
  if (window.location.pathname.startsWith('/admin')) return false;

  return true;
}

export function trackEvent(event: AnalyticsEvent, props?: Record<string, string | number>): void {
  if (!shouldTrack()) return;

  const payload = JSON.stringify({
    event,
    path: window.location.pathname,
    referrer: document.referrer || undefined,
    props: props ?? {},
  });

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Analytics must never break the UI.
  }
}
